// Client-side thumbnail generation (docs/10_ROADMAP.md #2.2). Pure DOM/canvas
// functions with no network and no store — the upload flow (lib/upload.ts) drives
// this right after a file finishes uploading, and the media detail drawer's
// "Generate preview" backfill button drives it again for pre-existing media.
//
// Contract this feeds (docs/03 §Media, backend-owned):
//   PUT /v1/media/:id/thumb   body: image/webp, <= 200 KB
// Both failure and success are silent to the caller — a thumb is a nice-to-have,
// never a reason to fail an upload or block the UI.

export const THUMB_MAX_EDGE = 480; // long edge, px
export const THUMB_MAX_BYTES = 200 * 1024; // must match the PUT /thumb cap
export const THUMB_MIME = 'image/webp';

// Quality ladder for canvas.toBlob: start high, step down until the cap is met.
const QUALITY_STEPS = [0.8, 0.65, 0.5, 0.35, 0.2];

// "seek to 1s (or 10% duration)" — take the smaller of the two so short clips
// don't try to seek past their own end.
const VIDEO_SEEK_MAX_S = 1;
const VIDEO_SEEK_FRACTION = 0.1;

/** Scale (w, h) so its long edge is at most maxEdge, preserving aspect ratio. */
export function computeThumbSize(
  w: number,
  h: number,
  maxEdge: number = THUMB_MAX_EDGE,
): { width: number; height: number } {
  const long = Math.max(w, h);
  if (long <= maxEdge || long <= 0) return { width: Math.round(w), height: Math.round(h) };
  const scale = maxEdge / long;
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

/** Draw `source` (already-decoded image/video frame) onto a size-capped canvas and
 * encode it as WebP, stepping quality down until it fits THUMB_MAX_BYTES. Returns
 * null if the source has no usable dimensions or every quality step still busts
 * the cap (caller treats null as "no thumb, upload proceeds normally"). */
async function drawToThumb(source: CanvasImageSource, naturalW: number, naturalH: number): Promise<Blob | null> {
  if (!naturalW || !naturalH) return null;
  const { width, height } = computeThumbSize(naturalW, naturalH);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);

  for (const quality of QUALITY_STEPS) {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), THUMB_MIME, quality));
    if (blob && blob.size <= THUMB_MAX_BYTES) return blob;
  }
  return null;
}

/** Image (or image-like blob) → capped WebP thumb. Honors EXIF orientation. */
export async function generateImageThumb(file: Blob): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null; // unsupported codec / corrupt file — no thumb, upload proceeds
  }
  try {
    return await drawToThumb(bitmap, bitmap.width, bitmap.height);
  } catch {
    return null;
  } finally {
    bitmap.close();
  }
}

/** Video blob → capped WebP thumb, grabbed from a frame near the start. */
export async function generateVideoThumb(file: Blob): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  try {
    return await new Promise<Blob | null>((resolve) => {
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = url;

      const settle = (blob: Blob | null) => resolve(blob);

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const target =
          Number.isFinite(duration) && duration > 0
            ? Math.min(VIDEO_SEEK_MAX_S, duration * VIDEO_SEEK_FRACTION)
            : 0;
        video.currentTime = target;
      };
      video.onseeked = () => {
        drawToThumb(video, video.videoWidth, video.videoHeight)
          .then(settle)
          .catch(() => settle(null));
      };
      video.onerror = () => settle(null);
    });
  } finally {
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
  }
}

/** Dispatch by media kind. Never throws — resolves null on any unsupported/failed case. */
export async function generateThumb(file: Blob, kind: 'image' | 'video'): Promise<Blob | null> {
  try {
    return kind === 'image' ? await generateImageThumb(file) : await generateVideoThumb(file);
  } catch {
    return null;
  }
}
